import React from 'react';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { PdfViewer } from 'src/design/organisms/PdfViewer/PdfViewer';
import { WorkflowButtons } from 'src/app/workflows/WorkflowButtons/WorkflowButtons';
import { NotificationComposer } from 'src/app/workflows/NotificationComposer/NotificationComposer';
import { RevokeLinksModal } from 'src/app/workflows/RevokeLinksModal';
import {
  NotificationCompositionRequest,
  NotificationPreviewDto,
  UserDto,
  WorkflowDto,
} from 'src/api/FiretrolApiClient';

export interface IWorkflowFormFooterProps {
  dirty: boolean;

  showButtons: boolean;
  error?: string | null;
  onSubmitClick: (nextStatus: string, nextStepIndex: number) => void;
  onSaveOnlyClick: () => void;
  onVoidClick: () => void;
  workflow: WorkflowDto | null;
  stepIndex: number;

  composerOpen: boolean;
  onComposerClose: () => void;
  preview: NotificationPreviewDto | null;
  jobUsers: UserDto[];
  customerNumber: string;
  composerSubmitting?: boolean;
  composerTitle?: string;
  composerToPlaceholder?: string;
  attachPdfLabel: string;
  onSend: (request: NotificationCompositionRequest) => void | Promise<void>;
  onSkip: (request: NotificationCompositionRequest) => void | Promise<void>;
  onReloadPreview?: (
    external: boolean,
  ) => Promise<NotificationPreviewDto | null>;

  previewOpen: boolean;
  onPreviewClose: () => void;
  /** Modal title; the inner viewer uses `${pdfTitle} preview`. */
  pdfTitle: string;
  previewUrl: string | null;
  previewLoading: boolean;

  revokeOpen: boolean;
  onRevokeClose: () => void;
  onRevokeConfirm: () => void;
  revoking: boolean;
  revokeEntityNoun: string;
  revokeItemsNoun: string;
}

/**
 * @registry Shared footer for the four workflow forms: the unsaved-changes badge,
 * WorkflowButtons, the notification composer modal, the PDF preview modal, and the
 * revoke-links modal. Callers own the composer/submission wiring and pass resolved props.
 */
export const WorkflowFormFooter: React.FC<IWorkflowFormFooterProps> = ({
  dirty,
  showButtons,
  error,
  onSubmitClick,
  onSaveOnlyClick,
  onVoidClick,
  workflow,
  stepIndex,
  composerOpen,
  onComposerClose,
  preview,
  jobUsers,
  customerNumber,
  composerSubmitting,
  composerTitle,
  composerToPlaceholder,
  attachPdfLabel,
  onSend,
  onSkip,
  onReloadPreview,
  previewOpen,
  onPreviewClose,
  pdfTitle,
  previewUrl,
  previewLoading,
  revokeOpen,
  onRevokeClose,
  onRevokeConfirm,
  revoking,
  revokeEntityNoun,
  revokeItemsNoun,
}) => (
  <>
    <Stack gap="xs">
      {dirty && (
        <Group justify="flex-end">
          <Badge color="yellow">Unsaved changes</Badge>
        </Group>
      )}
      {showButtons && (
        <WorkflowButtons
          error={error}
          onSubmitClick={onSubmitClick}
          workflow={workflow}
          onSaveOnlyClick={onSaveOnlyClick}
          onVoidClick={onVoidClick}
          stepIndex={stepIndex}
        />
      )}
    </Stack>

    <NotificationComposer
      opened={composerOpen}
      onClose={onComposerClose}
      preview={preview}
      jobUsers={jobUsers}
      customerNumber={customerNumber}
      submitting={composerSubmitting}
      title={composerTitle}
      toPlaceholder={composerToPlaceholder}
      attachPdfLabel={attachPdfLabel}
      onSend={onSend}
      onSkip={onSkip}
      onReloadPreview={onReloadPreview}
    />

    <BaseModal
      opened={previewOpen}
      onClose={onPreviewClose}
      title={pdfTitle}
      size="xl"
    >
      <PdfViewer
        src={previewUrl}
        title={`${pdfTitle} preview`}
        loading={previewLoading}
      />
    </BaseModal>

    <RevokeLinksModal
      opened={revokeOpen}
      onClose={onRevokeClose}
      onConfirm={onRevokeConfirm}
      revoking={revoking}
      entityNoun={revokeEntityNoun}
      itemsNoun={revokeItemsNoun}
    />
  </>
);
